import {
  Dialog,
  Box,
  IconButton,
  styled,
  Paper,
  IconButtonProps,
} from "@mui/material";

export const StyledDialog = styled(Dialog)(() => ({
  "& .MuiDialog-paper": {
    width: "100vw",
    height: "100vh",
    maxWidth: "100vw",
    maxHeight: "100vh",
    margin: 0,
    borderRadius: 0,
    padding: 0,
  },
}));

export const MainContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  width: "100%",
});

export const HeaderToolbar = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: theme.spacing(1, 2),
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  minHeight: "64px",
}));

export const LeftToolbar = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

export const RightToolbar = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

export const MainContent = styled(Box)({
  display: "flex",
  flex: 1,
  overflow: "hidden",
  position: "relative",
});

interface DraggableToolPanelProps {
  x: number;
  y: number;
}

export const DraggableToolPanel = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "x" && prop !== "y",
})<DraggableToolPanelProps>(({ theme, x, y }) => ({
  position: "absolute",
  left: x + "px",
  top: y + "px",
  width: "80px",
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: theme.spacing(1),
  gap: theme.spacing(1),
  zIndex: 1000,
  cursor: "move",
  boxShadow: theme.shadows[8],
  borderRadius: theme.shape.borderRadius,
  maxHeight: "calc(100vh - 200px)",
  overflowY: "auto",
}));

export const DragHandle = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "20px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  cursor: "grab",
  color: theme.palette.text.secondary,
  "&:active": {
    cursor: "grabbing",
  },
}));

export const CanvasContainer = styled(Box)({
  flex: 1,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  overflow: "hidden",
  position: "relative",
  backgroundColor: "#f5f5f5",
  margin: "32px",
});

export const FooterToolbar = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  padding: theme.spacing(1, 2),
  backgroundColor: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
  minHeight: "50px",
}));

export const FixedBottomToolbar = styled(Paper)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexWrap: "nowrap",
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.75, 1.5),
  backgroundColor: "#ffffffff",
  borderRadius: "30px",
  boxShadow: theme.shadows[8],
  margin: "0 auto",
  marginBottom: theme.spacing(1),
  width: "fit-content",
}));

interface ToolButtonProps extends IconButtonProps {
  active?: boolean;
}

export const ToolButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "active",
})<ToolButtonProps>(({ theme, active }) => ({
  backgroundColor: active ? theme.palette.action.selected : "transparent",
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1),
  width: "32px",
  height: "32px",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

export const ColorPickerContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
  alignItems: "center",
  marginTop: theme.spacing(1),
}));

interface ColorButtonProps {
  color: string;
  selected?: boolean;
}

export const ColorButton = styled(Box, {
  shouldForwardProp: (prop) => prop !== "selected",
})<ColorButtonProps>(({ color, selected, theme }) => ({
  width: 24,
  height: 24,
  backgroundColor: color,
  borderRadius: "50%",
  cursor: "pointer",
  border: selected
    ? `2px solid ${theme.palette.primary.main}`
    : `1px solid ${theme.palette.grey[300]}`,
  "&:hover": {
    transform: "scale(1.1)",
  },
  zIndex: 2,
}));

export const LoadingOverlay = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "rgba(255, 255, 255, 0.8)",
  zIndex: 1,
});

export const PopupPaper = styled(Paper)(({ theme }) => ({
  position: "absolute",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  padding: theme.spacing(1),
  borderRadius: "20px",
  backgroundColor: "#fff",
  zIndex: 10,
}));
